import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-fd-bg/70 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // CENTRING IS DELIBERATELY TRANSFORM-FREE. Pinned to all four edges
        // with automatic margins and a fit-content height, which centres on
        // both axes without touching `transform`.
        //
        // The obvious `left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2` is
        // what this used to do, and it is broken here: a CSS animation
        // outranks the author declarations it animates, so the instant
        // `animate-fd-scale-in` (keyframes ending at `transform: scale(1)`)
        // finished, both translates were wiped and the box sat with its top-
        // left corner at the centre of the screen. Keeping the box off
        // `transform` entirely means any entrance animation stays free to use
        // it, and `fd-scale-in` stays a generic animation rather than one that
        // has to hard-code this component's offsets.
        'fixed inset-0 z-50 m-auto grid h-fit w-full max-w-lg gap-4',
        'max-h-[calc(100dvh-2rem)] overflow-y-auto',
        'rounded-fd-lg border border-fd-border bg-fd-surface-1 p-6 text-fd-text',
        'shadow-fd-overlay',
        'data-[state=open]:animate-fd-scale-in',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className={cn(
          'absolute right-4 top-4 rounded-fd-sm p-1.5 text-fd-text-muted',
          'transition-colors hover:bg-fd-surface-2 hover:text-fd-text',
          'focus:outline-none focus:ring-2 focus:ring-fd-accent focus:ring-offset-2 focus:ring-offset-fd-bg',
        )}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex justify-end gap-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('font-geist text-[16px] font-medium tracking-[-0.01em] text-fd-text', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-[13px] text-fd-text-muted leading-relaxed', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
